// src/components/forms/FormRoot.tsx

import React, { useEffect, useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Tooltip,
    Box,
    useTheme,
    useMediaQuery,
    DialogProps
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightIcon from "@mui/icons-material/Nightlight";

import SaveStatusDialog from "../SaveStatusDialog";
import PanelRenderer from "./PanelRenderer";
import { FormProvider, useFormContext } from "./FormContext";
import { triggerUserEvent } from "./formEventEngine";
import { parsePageLayout } from "./layoutParser";
import {
    clearRules
} from "./RuleEngine";

import {
    TableField,
    TablePage,
    ParsedFormLayout,
    EventContext,
    FORM_EVENT_TARGET,
    Table,
} from "../../types/interfaces";

import API from "../../api/axiosInstance";
import { FormData } from "./formTypes";

interface Props {
    page: TablePage;
    mode: "ADD" | "EDIT" | "VIEW";
    recordData?: Record<string, any>;
    onClose: () => void;
    onSave?: (formData: Record<string, any>) => Promise<void>;
    inlineMode?: boolean; 
}

const FormContents: React.FC<{
    page: TablePage;
    layout: ParsedFormLayout;
    fields: TableField[];
    recordData: Record<string, any>;
    onClose: () => void;
    onSave?: (formData: FormData) => Promise<void>;
    mode: "ADD" | "EDIT" | "VIEW";
}> = ({ page, layout, fields, recordData, onClose, onSave, mode }) => {
    const theme = useTheme();
    const ctx = useFormContext();
    const isView = mode === "VIEW";

    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error">("saving");
    const [saveMessage, setSaveMessage] = useState<string>("");
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const handleSave = async () => {
        if (!ctx.validate()) return;
        setShowSaveDialog(true);
        setSaveStatus("saving");

        try {
            if (onSave) {
                await onSave(ctx.data);
            } else {
                throw new Error("No save handler provided.");
            }
            setSaveStatus("success");
            setTimeout(() => {
                setShowSaveDialog(false);
                onClose();
            }, 1200);
        } catch (err: any) {
            console.error("❌ Error in onSave:", err);
            setSaveStatus("error");
            setSaveMessage(err?.message || "Save failed unexpectedly.");
        }
    };

    const handleCancel = useCallback(() => {
        if (ctx?.touched && Object.values(ctx.touched).some(Boolean)) {
            setShowCancelConfirm(true);
        } else {
            onClose();
        }
    }, [ctx, onClose]);

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        onClose();
    };

    return (
        <>
            <DialogContent dividers sx={{ backgroundColor: theme.palette.background.paper }}>
                {!layout ? (
                    <Box p={2} bgcolor="error.light" borderRadius={2}>
                        <Typography color="error.contrastText">
                            This form layout could not be rendered. Please check the design or configuration.
                        </Typography>
                    </Box>
                ) : (
                    layout.rootPanels.map((panel, i) => (
                        <PanelRenderer
                            key={panel.id || i}
                            panel={panel}
                            allFields={fields}
                            recordData={recordData}
                        />
                    ))
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                {!isView && (
                    <Button variant="contained" onClick={handleSave}>
                        {mode === "EDIT" ? "Save Changes" : "Create Record"}
                    </Button>
                )}
            </DialogActions>

            <SaveStatusDialog
                open={showSaveDialog}
                status={saveStatus}
                message={saveMessage}
                onClose={() => setShowSaveDialog(false)}
            />

            <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
                <DialogTitle>Discard Changes?</DialogTitle>
                <DialogContent>
                    <Typography>You have unsaved changes. Are you sure you want to discard them?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCancelConfirm(false)}>Go Back</Button>
                    <Button onClick={confirmCancel} color="error">Discard</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};


const FormRoot: React.FC<Props> = ({ page, mode, recordData = {}, onClose, onSave, inlineMode = false }) => {
    const theme = useTheme();
    const [layout, setLayout] = useState<ParsedFormLayout | null>(null);
    const [fields, setFields] = useState<TableField[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const bootstrap = async () => {
            try {
                setLoading(true);
                clearRules();

                const user = JSON.parse(localStorage.getItem('user') || "{}");
                const headers = {
                    headers: {
                        authToken: user.user?.authtoken,
                        sessionId: user.user?.sessionId
                    }
                };

                const tableRes = await API.get(`/tables/${page.tableID}`, headers);
                const table: Table = tableRes.data;

                const tableFields = table.fields || [];
                setFields(tableFields);

                const parsed = parsePageLayout(page.layout);
                if (!parsed || !parsed.rootPanels?.length) {
                    throw new Error("Invalid or empty layout");
                }

                setLayout(parsed);

                triggerUserEvent(FORM_EVENT_TARGET, "OnLoad", {
                    pageId: page.pageID,
                    tableId: page.tableID,
                    mode,
                    recordData
                } as EventContext);
            } catch (err) {
                console.error("Failed to load layout or fields:", err);
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, [page]);

    if (loading || !layout) return null;

    return (
        <FormProvider mode={mode} fields={fields} initialData={recordData}>
            {inlineMode ? (
                <Box
                    p={3}
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                            {page.pageName}
                        </Typography>
                        <Box display="flex" gap={1}>
                            {mode === "VIEW" && (
                                <Tooltip title="Print / Export">
                                    <IconButton onClick={() => alert("Export coming soon")}>
                                        <PrintIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Toggle Theme">
                                <IconButton onClick={() => alert("Theme toggle coming soon")}>
                                    {theme.palette.mode === "dark" ? <WbSunnyIcon /> : <NightlightIcon />}
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <FormContents
                        page={page}
                        layout={layout}
                        fields={fields}
                        recordData={recordData}
                        onClose={onClose}
                        onSave={onSave}
                        mode={mode}
                    />
                </Box>
            ) : (
                <Dialog open fullWidth maxWidth="md" scroll="paper" aria-labelledby="form-dialog-title">
                    <Box
                        sx={{
                            padding: "16px 24px",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: theme.palette.background.default
                        }}
                    >
                        <Typography id="form-dialog-title" variant="h6" sx={{ fontWeight: 600 }}>
                            {page.pageName}
                        </Typography>

                        <Box display="flex" gap={1}>
                            {mode === "VIEW" && (
                                <Tooltip title="Print / Export">
                                    <IconButton onClick={() => alert("Export coming soon")}>
                                        <PrintIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Toggle Theme">
                                <IconButton onClick={() => alert("Theme toggle coming soon")}>
                                    {theme.palette.mode === "dark" ? <WbSunnyIcon /> : <NightlightIcon />}
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <FormContents
                        page={page}
                        layout={layout}
                        fields={fields}
                        recordData={recordData}
                        onClose={onClose}
                        onSave={onSave}
                        mode={mode}
                    />
                </Dialog>
            )}
        </FormProvider>
    );
};

export default FormRoot;
